import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApiRequest } from "../utils/adminApi";

export default function StudioBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const sortedBookings = useMemo(() => {
    return [...bookings].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  }, [bookings]);

  const formatDate = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    return d.toLocaleDateString("en-GB");
  };

  const formatDateTime = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await adminApiRequest({
        script: "list_studio_bookings",
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load bookings.");
      }

      setBookings(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.message || "Unable to load bookings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  return (
    <section className="h-full min-h-0">
      <div className="flex h-full min-h-0 flex-col rounded-3xl border border-slate-200 bg-white shadow-sm">
        {/* Header */}
        <div className="border-b border-slate-200 p-5">
          <h2 className="text-2xl font-semibold text-slate-900">
            Studio Session Bookings
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            View all completed (paid) studio bookings.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="px-5 pt-4">
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="min-h-0 flex-1 overflow-auto p-5">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
              Loading bookings...
            </div>
          ) : sortedBookings.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center">
              <p className="text-sm text-slate-500">No paid bookings yet.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead className="bg-slate-50">
                    <tr className="text-sm text-slate-600">
                      <th className="px-4 py-3 font-semibold">Ref</th>
                      <th className="px-4 py-3 font-semibold">Customer</th>
                      <th className="px-4 py-3 font-semibold">Package</th>
                      <th className="px-4 py-3 font-semibold">Session</th>
                      <th className="px-4 py-3 font-semibold">Notes</th>
                      <th className="px-4 py-3 font-semibold">Date</th>
                      <th className="px-4 py-3 font-semibold">Time</th>
                      <th className="px-4 py-3 font-semibold">Amount</th>
                      <th className="px-4 py-3 font-semibold">Paid At</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200 bg-white">
                    {sortedBookings.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-4 text-sm font-medium text-slate-900">
                          {item.booking_ref}
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700">
                          <div className="font-medium text-slate-900">
                            {item.customer_name}
                          </div>

                          <div className="text-xs text-slate-500">
                            {item.customer_email}
                          </div>

                          {item.customer_phone && (
                            <a
                              href={`tel:${item.customer_phone}`}
                              className="text-xs text-indigo-600 hover:underline"
                            >
                              {item.customer_phone}
                            </a>
                          )}
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700">
                          {item.package_name}
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700">
                          <div>{item.session_duration}</div>

                          <div className="text-xs text-slate-500">
                            {item.edited_images} edited images
                          </div>

                          {item.extra && (
                            <div className="mt-1 text-xs text-slate-500">
                              {item.extra}
                            </div>
                          )}
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700">
                          {item.notes ? (
                            <div className="text-xs text-indigo-600 whitespace-pre-wrap">
                              {item.notes}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700">
                          {formatDate(item.booking_date)}
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700">
                          {item.booking_time}
                        </td>

                        <td className="px-4 py-4 text-sm font-medium text-slate-900">
                          £{Number(item.amount).toFixed(2)}
                        </td>

                        <td className="px-4 py-4 text-sm font-medium text-green-600">
                          {formatDateTime(item.paid_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
